
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import SuperAdminHeader from '@/components/superadmin/SuperAdminHeader';
import { useToast } from '@/hooks/use-toast';
import { useSuperAdminSettings } from '@/hooks/useSuperAdminSettings';
import { useResellerPlanData } from '@/hooks/useResellerPlanData';
import { ActivePlan } from '@/hooks/license/useResellerPlanManagement';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import ResellerPlansSyncButton from '@/components/superadmin/reseller/ResellerPlansSyncButton';
import ResellerPlanOverview from '@/components/superadmin/reseller/ResellerPlanOverview';
import ResellerDataSection from '@/components/superadmin/reseller/ResellerDataSection';
import ResellerAdminSection from '@/components/superadmin/reseller/ResellerAdminSection';
import { MasterLicenseSettingsProvider } from '@/contexts/MasterLicenseSettingsContext';

const SuperAdminResellerPlans = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const {
    isLoading: isSettingsLoading,
    user,
    handleLogout
  } = useSuperAdminSettings();

  // Get local reseller plan data
  const {
    currentPlan: localPlan,
    currentRevenueData,
    activePlans: initialActivePlans,
    adminAccounts,
    revenueHistory,
    isLoading: isPlanDataLoading
  } = useResellerPlanData();

  // Get master license reseller plans
  const { resellerPlans, isLoadingPlans } = useResellerPlans(false);
  
  // Use the first master plan if available
  const masterPlan = resellerPlans.length > 0 ? resellerPlans[0] : null;
  
  // Combine the data, preferring master plan data when available
  const currentPlan = masterPlan || localPlan;
  
  const [activePlans, setActivePlans] = useState<ActivePlan[]>([
    {
      id: 'plan-1',
      name: currentPlan.name || 'Reseller Pro',
      status: 'Active',
      admins: currentPlan.maxAdmins || 3,
      users: currentPlan.maxUsers || 250,
      enabled: true
    }
  ]);

  // Update local plans when master plans change
  useEffect(() => {
    if (masterPlan) {
      setActivePlans([
        {
          id: masterPlan.id || 'plan-1',
          name: masterPlan.name,
          status: 'Active',
          admins: masterPlan.maxAdmins,
          users: masterPlan.maxUsers,
          enabled: true
        }
      ]);
    }
  }, [masterPlan]);

  const handleTogglePlanEnabled = (planId: string, enabled: boolean) => {
    setActivePlans(prevPlans => 
      prevPlans.map(plan => 
        plan.id === planId ? { ...plan, enabled } : plan
      )
    );
    
    toast({
      title: enabled ? "Plan Enabled" : "Plan Disabled",
      description: `${activePlans.find(p => p.id === planId)?.name} has been ${enabled ? 'enabled' : 'disabled'}`,
    });
  };

  const handleSyncCompleted = () => {
    // You can add additional logic here if needed after sync
    console.log("Sync completed successfully");
  };

  const isLoading = isSettingsLoading || isPlanDataLoading || isLoadingPlans;

  const currentPlanForCard: ActivePlan = {
    id: 'current-plan',
    name: currentPlan.name,
    status: 'Active',
    admins: currentPlan.maxAdmins,
    users: currentPlan.maxUsers,
    enabled: true
  };

  return (
    <SuperAdminLayout
      isLoading={isLoading}
      onLogout={handleLogout}
      userRole={user?.role}
      activeTab="reseller-plans"
    >
      <MasterLicenseSettingsProvider isLoading={isLoading}>
        <SuperAdminHeader 
          title="Reseller Pro Plan" 
          description="View your reseller plan details and revenue sharing" 
          userRole={user?.role}
        />
        
        <div className="flex justify-between items-center mb-4">
          <ResellerPlansSyncButton onSync={handleSyncCompleted} />
        </div>
        
        <ResellerPlanOverview
          currentPlan={currentPlan}
          currentRevenueData={currentRevenueData}
        />
        
        <ResellerDataSection
          revenueHistory={revenueHistory}
          activePlans={activePlans}
          currentPlan={currentPlanForCard}
          onTogglePlanEnabled={handleTogglePlanEnabled}
        />
        
        <ResellerAdminSection
          currentPlan={currentPlan}
          adminAccounts={adminAccounts}
        />
      </MasterLicenseSettingsProvider>
    </SuperAdminLayout>
  );
};

export default SuperAdminResellerPlans;
